import { ControlsOperation, type ICameraControls, type TControlMouse, ControlKey } from '@/commons/camera/controls/ICameraControls';
// eslint-disable-next-line import/extensions
import { OrbitControls } from 'three/addons/controls/OrbitControls.js';
import { MOUSE, type Camera } from 'three';

type MouseAction = 'LEFT' | 'MIDDLE' | 'RIGHT';
type KeyAction = 'CONTROL' | 'SHIFT' | 'default';

class CustomOrbitControls extends OrbitControls implements ICameraControls {
    private _mouseActions: {
        [key in KeyAction]: {
            [action in MouseAction]?: MOUSE;
        };
    } = {
        default: {
            LEFT: MOUSE.ROTATE,
            MIDDLE: MOUSE.DOLLY,
            RIGHT: MOUSE.PAN,
        },
        SHIFT: {
            LEFT: MOUSE.ROTATE,
            MIDDLE: MOUSE.DOLLY,
            RIGHT: MOUSE.PAN,
        },
        CONTROL: {
            LEFT: MOUSE.ROTATE,
            MIDDLE: MOUSE.DOLLY,
            RIGHT: MOUSE.PAN,
        },
    };

    private static getMouseAction = (mouse: string | number): MouseAction => {
        switch (mouse) {
            case 0:
                return 'LEFT';
            case 1:
                return 'MIDDLE';
            default:
                return 'RIGHT';
        }
    };

    private static getKeyAction(key: string | undefined): KeyAction {
        switch (key) {
            case ControlKey.CTRL:
                return 'CONTROL';
            case ControlKey.SHIFT:
                return 'SHIFT';
            default:
                return 'default';
        }
    }

    private static getOperationAction(operation: string): MOUSE | undefined {
        switch (operation) {
            case ControlsOperation.ZOOM:
                return MOUSE.DOLLY;
            case ControlsOperation.ROTATE:
                return MOUSE.ROTATE;
            case ControlsOperation.PAN:
                return MOUSE.PAN;
            default:
                return undefined;
        }
    }

    constructor(object: Camera, domElement: HTMLElement) {
        super(object, domElement);

        window.addEventListener('keydown', this.#onKeyDownCallback);
        window.addEventListener('keyup', this.#onKeyUpCallback);
    }

    #onKeyDownCallback = (event: KeyboardEvent) => {
        const { SHIFT, CONTROL } = this._mouseActions;
        if (event.shiftKey) {
            this.mouseButtons = {
                LEFT: SHIFT.LEFT,
                MIDDLE: SHIFT.MIDDLE,
                RIGHT: SHIFT.RIGHT,
            };
        } else if (event.ctrlKey) {
            this.mouseButtons = {
                LEFT: CONTROL.LEFT,
                MIDDLE: CONTROL.MIDDLE,
                RIGHT: CONTROL.RIGHT,
            };
        }
    };

    #onKeyUpCallback = (event: KeyboardEvent) => {
        if (event.key === 'Shift' || event.key === 'Control') {
            this.updateDefautMouseActions();
        }
    };

    private updateDefautMouseActions() {
        const { LEFT, RIGHT, MIDDLE } = this._mouseActions.default;
        this.mouseButtons = {
            LEFT,
            MIDDLE,
            RIGHT,
        };
    }

    dispose(): void {
        window.removeEventListener('keydown', this.#onKeyDownCallback);
        window.removeEventListener('keyup', this.#onKeyUpCallback);

        super.dispose();
    }

    addMouseAction(operation: ControlsOperation, mouse: TControlMouse, key?: ControlKey | undefined): void {
        const keyAction = CustomOrbitControls.getKeyAction(key);
        const mouseAction = CustomOrbitControls.getMouseAction(mouse);
        const action = CustomOrbitControls.getOperationAction(operation);

        this._mouseActions[keyAction][mouseAction] = action;
        this.updateDefautMouseActions();
    }

    removeMouseAction(mouse: TControlMouse, key?: ControlKey | undefined): boolean {
        const keyAction = CustomOrbitControls.getKeyAction(key);
        const mouseAction = CustomOrbitControls.getMouseAction(mouse);
        this._mouseActions[keyAction][mouseAction] = undefined;
        this.updateDefautMouseActions();
        return true;
    }
}
export default CustomOrbitControls;
